import React, { Component, ErrorInfo, ReactNode } from 'react';
import { errorMonitoring } from '../../utils/errorMonitoring';
import { EliteError } from './EliteError';
import i18n from '../../i18n';
import logger from '../../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to monitoring service
    errorMonitoring.captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    logger.error('Error caught by boundary:', error, errorInfo);
    
    this.setState({
      errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Elite fallback UI
      return (
        <EliteError
          variant="generic"
          title={i18n.t('errorGenericTitle')}
          error={i18n.t('errorGenericMessage')}
          onRetry={this.handleRetry}
          onHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
